import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardActionArea,
  CircularProgress,
  Alert as MuiAlert,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Refresh,
  DoneAll,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { getCalAvatar } from '../config/calAvatars';
import { CalAlert, deriveAlerts, mapServerNotifications, loadReadIds, saveReadIds } from '../utils/calAlerts';
import DigestBuilder from './DigestBuilder';

const API_ENDPOINT =
  process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

interface NotificationCenterProps {
  onMeterSelect?: (meterId: string) => void;
}

type SeverityFilter = 'all' | 'critical' | 'warning' | 'info';

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onMeterSelect }) => {
  const theme = useTheme();
  const { getIdTokenClaims, user } = useAuth0();
  const email = user?.email;

  const [alerts, setAlerts] = useState<CalAlert[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds(email));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [tab, setTab] = useState(0); // 0 = Alerts, 1 = Email Digests

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const headers = { Authorization: `Bearer ${token}` };
      const [dashResp, notifResp] = await Promise.all([
        fetch(`${API_ENDPOINT}/meters/dashboard`, { headers }),
        fetch(`${API_ENDPOINT}/notifications`, { headers }),
      ]);
      if (!dashResp.ok) throw new Error('Failed to load site data');
      const data = await dashResp.json();
      const derived = deriveAlerts(data.meter_products || []); // live health + drift
      let server: CalAlert[] = [];
      if (notifResp.ok) {
        const nj = await notifResp.json();
        server = mapServerNotifications(nj.notifications || []); // persisted events
      }
      // Seed read state from any server-side-read items (cross-device).
      if (server.some((s) => s.read)) {
        const seeded = loadReadIds(email);
        server.forEach((s) => s.read && seeded.add(s.id));
        setReadIds(seeded);
        saveReadIds(email, seeded);
      }
      const rank = (s: string) => (s === 'critical' ? 0 : s === 'warning' ? 1 : 2);
      const merged = [...derived, ...server].sort(
        (a, b) => rank(a.severity) - rank(b.severity) || b.timestamp.getTime() - a.timestamp.getTime(),
      );
      setAlerts(merged);
    } catch (e: any) {
      setError(e?.message || 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [getIdTokenClaims, email]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    setReadIds(loadReadIds(email));
    // Keep in sync when read state changes elsewhere (e.g. the bell).
    const onChange = () => setReadIds(loadReadIds(email));
    window.addEventListener('cal-notif-read-changed', onChange);
    return () => window.removeEventListener('cal-notif-read-changed', onChange);
  }, [email]);

  const persistRead = (next: Set<string>) => {
    setReadIds(new Set(next));
    saveReadIds(email, next);
  };

  const syncServerRead = async (path: string) => {
    try {
      const token = (await getIdTokenClaims())?.__raw;
      await fetch(`${API_ENDPOINT}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } catch {
      /* best-effort — localStorage already reflects the read state */
    }
  };

  const markRead = (id: string) => {
    if (readIds.has(id)) return;
    const next = new Set(readIds);
    next.add(id);
    persistRead(next);
    if (alerts.find((a) => a.id === id)?.server) {
      syncServerRead(`/notifications/${encodeURIComponent(id)}/read`);
    }
  };

  const markAllRead = () => {
    const next = new Set(readIds);
    alerts.forEach((a) => next.add(a.id));
    persistRead(next);
    if (alerts.some((a) => a.server)) syncServerRead('/notifications/read-all');
  };

  const withRead = useMemo(
    () => alerts.map((a) => ({ ...a, read: readIds.has(a.id) })),
    [alerts, readIds],
  );

  const counts = useMemo(() => {
    const c = { total: withRead.length, unread: 0, critical: 0, warning: 0, info: 0 };
    withRead.forEach((a) => {
      if (!a.read) c.unread += 1;
      c[a.severity] += 1;
    });
    return c;
  }, [withRead]);

  const visible = useMemo(
    () =>
      withRead.filter(
        (a) => (severity === 'all' || a.severity === severity) && (!unreadOnly || !a.read),
      ),
    [withRead, severity, unreadOnly],
  );

  const iconFor = (s: string) =>
    s === 'critical' ? (
      <ErrorIcon sx={{ color: theme.palette.error.main }} />
    ) : s === 'warning' ? (
      <WarningIcon sx={{ color: theme.palette.warning.main }} />
    ) : (
      <CheckCircleIcon sx={{ color: theme.palette.info.main }} />
    );

  const colorFor = (s: string) =>
    s === 'critical'
      ? theme.palette.error.main
      : s === 'warning'
      ? theme.palette.warning.main
      : theme.palette.info.main;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Notification Center
            </Typography>
            {counts.unread > 0 && (
              <Chip label={`${counts.unread} unread`} color="error" size="small" sx={{ fontWeight: 600 }} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every warning Cal is raising across your site — control-limit health and regression drift.
          </Typography>
        </Box>
        {tab === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              startIcon={<DoneAll />}
              onClick={markAllRead}
              disabled={counts.unread === 0}
              sx={{ textTransform: 'none' }}
            >
              Mark all read
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchAlerts} disabled={loading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Alerts" />
        <Tab label="Email Digests" />
      </Tabs>

      {tab === 0 && (
        <>
      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={severity}
          onChange={(_e, v) => setSeverity((v as SeverityFilter) || 'all')}
        >
          <ToggleButton value="all">All {counts.total}</ToggleButton>
          <ToggleButton value="critical" sx={{ color: 'error.main' }}>
            Critical {counts.critical}
          </ToggleButton>
          <ToggleButton value="warning" sx={{ color: 'warning.main' }}>
            Warning {counts.warning}
          </ToggleButton>
          <ToggleButton value="info" sx={{ color: 'info.main' }}>
            Watch {counts.info}
          </ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Switch size="small" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />}
          label="Unread only"
        />
      </Box>

      {error && (
        <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {/* List */}
      {loading && withRead.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {counts.total === 0 ? 'All meters operating normally' : 'Nothing matches this filter'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cal is monitoring your site 24/7.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visible.map((a) => (
            <Card
              key={a.id}
              variant="outlined"
              sx={{
                borderLeft: `4px solid ${colorFor(a.severity)}`,
                bgcolor: a.read ? 'transparent' : alpha(colorFor(a.severity), 0.05),
              }}
            >
              <CardActionArea
                onClick={() => {
                  markRead(a.id);
                  if (a.meterId && onMeterSelect) onMeterSelect(a.meterId);
                }}
                sx={{ p: 2 }}
              >
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ pt: 0.25 }}>{iconFor(a.severity)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {a.title}
                      </Typography>
                      {!a.read && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorFor(a.severity) }} />
                      )}
                      <Chip
                        label={a.kindLabel || (a.kind === 'drift' ? 'Regression' : 'Control limits')}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {a.message}
                    </Typography>
                    {a.recommendation && (
                      <MuiAlert
                        severity={a.severity === 'critical' ? 'error' : a.severity}
                        icon={false}
                        sx={{ mt: 1, py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <img
                            src={getCalAvatar().path}
                            alt="Cal"
                            style={{ width: 16, height: 16, objectFit: 'cover', borderRadius: '50%' }}
                          />
                          {a.recommendation}
                        </Box>
                      </MuiAlert>
                    )}
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
        </>
      )}

      {tab === 1 && <DigestBuilder />}
    </Box>
  );
};

export default NotificationCenter;
